import { useEffect, useMemo, useRef, useState } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { CARDS } from "@/data/goalsBoard";
import { useBoard } from "@/hooks/useBoard";
import { act as ccAct } from "@/lib/ccApi";
import { useDeskFlash } from "@/shell/DeskFlashContext";
import { EngineBay } from "@/screens/desk/EngineBay";
import { QueueLane } from "@/screens/desk/QueueLane";
import { ReviewBay } from "@/screens/desk/ReviewBay";
import { DeskTasks } from "@/screens/desk/DeskTasks";
import KitCapacity from "@/screens/cc/KitCapacity";
import { EventRow } from "@/screens/shared/EventRow";
import { Composer } from "@/screens/desk/Composer";
import { AddWorkBar } from "@/screens/cc/AddWorkBar";
import { CaptureExtras } from "@/screens/idea/CaptureExtras";
import { Card, Label, MonoMeta, StateWord } from "@/design/primitives";
import { cn } from "@/lib/utils";

// BLANK SLATE (Jeff 10/30): testing data out of the desk until he puts real
// content in. One flag to flip false when the first real project lands here.
const BLANK_SLATE = false; // flipped 10/30: the dispatch loop is live — real work lands in the bay

// Blank bay placeholder (Jeff 10/30: fake content out — real projects next).
const PLACEHOLDER = {
  slug: "bay-placeholder",
  title: "Nothing in motion — Kit is ready for something new",
  brand: "Agentic OS",
  rawState: "EXECUTE",
  stateWord: "Standing by",
  next: "Drop an idea in the box on the right — it becomes the next card here.",
  blockersLine: "Nothing blocking — the bay is clear",
};

// ── Desk (v3, Jeff 10/29 redesign) ──────────────────────────────────────────
// ORDER PER JEFF 10/30 (Desk 2 build):
//   1. Kit capacity — compact, top of page (bar + pct + slots only)
//   2. Two columns: Engine Bay titles (left, larger) | +Idea composer (right)
//   3. Next up
//   4. Awaiting review
// No StartHere, no KitNowLine, no side-panel detail overlay.

const ActChip = ({ label, busy, onClick, primary, done }) => (
  <button
    type="button"
    data-testid="desk-act-chip"
    data-done={done ? "true" : undefined}
    disabled={busy || done}
    onClick={onClick}
    className={cn(
      "rounded-md border px-2.5 py-1 font-mono text-[0.71875rem] transition-all duration-200 disabled:opacity-50",
      done
        ? "border-os-green bg-os-green text-white"
        : primary
          ? "border-os-border-strong bg-os-ink text-white hover:opacity-90"
          : "border-os-border bg-white text-os-ink hover:border-os-border-strong",
    )}
  >
    {done ? "✓ Done" : busy ? "Working…" : label}
  </button>
);

function useAgoTick() {
  const [, setT] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setT((x) => x + 1), 30000);
    return () => clearInterval(t);
  }, []);
}


// ── Feed ──
const WORK_KINDS = new Set([
  "moved",
  "progress",
  "resolved",
  "picked_up",
  "needs_you",
  "finished",
  "failed",
]);
const FeedChips = ({ filter, setFilter, counts }) => {
  const chip = (key, label) => (
    <button
      key={key}
      type="button"
      data-testid={`desk-feed-chip-${key}`}
      onClick={() => setFilter(key)}
      className={cn(
        "rounded-chip border px-2.5 py-1 font-mono text-[0.6875rem] uppercase tracking-[0.08em] transition-colors",
        filter === key
          ? "border-os-border-strong bg-os-ink text-white"
          : "border-os-border bg-white text-os-ink-3 hover:border-os-border-strong hover:text-os-ink",
      )}
    >
      {label}
      {typeof counts[key] === "number" && (
        <span className="ml-1 opacity-70">{counts[key]}</span>
      )}
    </button>
  );
  return (
    <div
      data-testid="desk-feed-chips"
      className="flex flex-wrap items-center gap-1.5"
    >
      {chip("all", "All")}
      {chip("work", "Work")}
      {chip("finished", "Finished")}
      {chip("kit", "Kit")}
    </div>
  );
};

const filterChipMoreN = (moreN, events) => moreN;
const pacePerHour = (events) => {
  const hourAgo = Date.now() - 3600000;
  const ticks = (events || []).filter(
    (e) =>
      (e.kind === "beat" || e.kind === "moved" || e.kind === "progress") &&
      new Date(e.at).getTime() >= hourAgo,
  ).length;
  return ticks;
};

export const DeskFeed = ({
  events,
  activity,
  working,
  moreN,
  beatsPerHour,
}) => {
  const [filter, setFilter] = useState("all");
  const [beatsOpen, setBeatsOpen] = useState(false);
  const beatRows = (events || []).filter((e) => e.kind === "beat");
  const workFeed = (events || []).filter((e) => e.kind !== "beat");
  const finished = workFeed.filter(
    (e) => e.kind === "resolved" || e.kind === "finished",
  );
  const counts = {
    all: workFeed.length,
    finished: finished.length,
    work: workFeed.length - finished.length,
    kit: (activity || []).length,
  };
  const shown =
    filter === "finished"
      ? finished
      : filter === "all" || filter === "work"
        ? workFeed
        : [];
  const kitShown =
    filter === "kit" || filter === "all" ? (activity || []).slice(0, 8) : [];
  return (
    <section data-testid="desk-feed" className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Label as="h2">The last stretch — everything that changed</Label>
        <FeedChips filter={filter} setFilter={setFilter} counts={counts} />
      </div>
      {shown.length === 0 && kitShown.length === 0 ? (
        <Card quiet className="px-5 py-4">
          <p className="text-[0.875rem] text-os-ink-2">
            Nothing to show under this chip — flip chips or come back after the
            next pass.
          </p>
        </Card>
      ) : (
        <>
          {shown.length > 0 && (
            <ul className="flex flex-col gap-1.5" data-testid="desk-feed-rows">
              {shown.map((e) => (
                <EventRow key={e.id} e={e} deskAnim />
              ))}
            </ul>
          )}
          {kitShown.length > 0 && (
            <div data-testid="desk-kit-ledger" className="flex flex-col gap-1">
              <MonoMeta as="p">Kit's own ledger — latest first</MonoMeta>
              {kitShown.map((a, i) => (
                <p
                  key={i}
                  className="border-t border-os-border py-1.5 text-[0.8125rem] text-os-ink-2"
                >
                  <span className="mr-2 font-mono text-[0.6875rem] text-os-ink-3">
                    {timeAgo(a.at)}
                  </span>
                  {a.verb}
                  {a.note ? ` — ${a.note}` : ""}
                </p>
              ))}
            </div>
          )}
        </>
      )}
      {beatRows.length > 0 && (
        <button
          type="button"
          data-testid="desk-beats-toggle"
          onClick={() => setBeatsOpen((v) => !v)}
          className="self-start font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-os-ink-3 underline underline-offset-4 hover:text-os-ink"
        >
          {beatsOpen
            ? "Hide the watcher beats"
            : `${beatRows.length} watcher beats${beatsPerHour ? ` · ${beatsPerHour} hourly pace` : ""} — show`}
        </button>
      )}
      {beatsOpen && (
        <ul className="flex flex-col gap-1.5" data-testid="desk-beat-rows">
          {beatRows.map((e) => (
            <EventRow key={e.id} e={e} />
          ))}
        </ul>
      )}
      {filter !== "kit" && moreN > 0 && (
        <div className="flex flex-col gap-1.5">
          {working.slice(1).map((c) => (
            <div
              key={c.slug}
              className="flex items-baseline gap-3 rounded-card border border-os-border px-4 py-2.5"
              data-testid="desk-shoulders-row"
            >
              <StateWord tone={c.state === "EXECUTE" ? "green" : "amber"}>
                {c.state === "EXECUTE" ? "working" : "checking"}
              </StateWord>
              <p className="min-w-0 flex-1 truncate text-[0.875rem] text-os-ink">
                {(c.payload || {}).title}
              </p>
              {typeof (c.payload || {}).next === "string" && (
                <p className="hidden min-w-0 flex-[2] truncate text-[0.8125rem] text-os-ink-2 sm:block">
                  Next: {String(c.payload.next).replace(/^Next: /, "")}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

// ── +Idea composer (Jeff 10/30 Desk build): the large text box with the mic
// and attach icons and the Start It button — the same intake the Goal Board
// uses, wired here so dictation and files ride along.
const RefillComposer = () => {
  const fillRef = useRef(null);
  const attachIdsRef = useRef(null);
  const [flash, setFlash] = useState(null);
  return (
    <div className="flex h-full flex-col gap-2" data-testid="desk-idea-composer">
      {flash && (
        <p data-testid="desk-idea-composer-flash" className="text-[0.8125rem] font-medium text-os-green">
          {flash}
        </p>
      )}
      <AddWorkBar
        tall
        registerFill={(fn) => { fillRef.current = fn; }}
        registerAttachIds={(fn) => { attachIdsRef.current = fn; }}
        onAdded={() => setFlash("Added — it's on the board as a new idea. Kit picks it up from here.")}
      />
      <CaptureExtras
        onTranscript={(text) => fillRef.current?.(text)}
        registerAttachIds={(fn) => { attachIdsRef.current = fn; }}
        onAttached={(f) => setFlash(`Attached “${f.name}” — it ships with this idea.`)}
      />
      <Composer />
    </div>
  );
};

export default function DeskPage() {
  const desktop = useMediaQuery("(min-width: 1024px)");
  const goals = useBoard("goals");
  const mail = useBoard("mail");
  const projects = useBoard("projects");
  const cities = useBoard("cities");
  const kitstatus = useBoard("kitstatus");
  const [flash, setFlash] = useDeskFlash();
  useAgoTick();

  const live = goals.live;
  // Bay feed: goal rows PLUS dispatched project rows (Jeff 10/30 dispatch loop —
  // a Start It idea graduates to a projects row that must land IN the bay).
  // Jeff 11-04: parked (TABLED) dispatched rows ride along too — the park
  // button moves them off the projects board, and without this they would
  // vanish from the desk instead of showing up under On hold.
  const goalRows = useMemo(
    () =>
      (goals.items || [])
        .filter((r) => r.kind === "goal")
        .concat(
          (projects.items || [])
            .filter(
              (r) =>
                r.kind === "project" &&
                (r.payload || {}).dispatch &&
                ["EXECUTE", "TEST", "CLOSE", "TABLED"].includes(r.state),
            )
            .map((r) => ({
              ...r,
              kind: "goal",
              // Jeff 11-04: remember the row's real board — On hold's verbs
              // (resume/archive/reorder) must act on the projects board for
              // these rows, not the goals board (the dead-park bug).
              boardSrc: "projects",
              payload: { ...r.payload, stateWord: r.state === "TABLED" ? "Parked" : "Working" },
            })),
        ),
    [goals.items, projects.items],
  );

  const liveGoalCards = useMemo(
    () =>
      goalRows.map((r) => {
        const p = r.payload || {};
        const approved = (p.jeff || {}).approved;
        return {
          ...p,
          slug: r.slug,
          rawState: r.state,
          stateWord: approved ? "Approved — wrapping up" : p.stateWord,
          next: approved
            ? "Next: Kit finishes the wrap-up — archive pass, notes, and the work ledger"
            : p.next,
          blockersLine: approved
            ? "Nothing waiting on you anymore"
            : p.blockersLine,
        };
      }),
    [goalRows],
  );
  const goalCards = live ? liveGoalCards : CARDS;

  const [expandedTileSlug, setExpandedTileSlug] = useState(null);
  // Jeff 10/30: blank-content mode — testing data is out; the bay shows one
  // open placeholder card saying Kit is ready, and nothing else in the bays.
  const blankBay = BLANK_SLATE || !live || liveGoalCards.length === 0;
  useEffect(() => {
    if (expandedTileSlug !== null) return;
    if (blankBay) {
      setExpandedTileSlug(PLACEHOLDER.slug);
      return;
    }
    const firstActive = (goalCards || []).find((c) =>
      ["EXECUTE", "TEST", "CLOSE"].includes(c.rawState),
    );
    if (firstActive?.slug) setExpandedTileSlug(firstActive.slug);
  }, [goalCards, blankBay, expandedTileSlug]);

  // Events

  const cap = (kitstatus.items || []).find((r) => r.slug === "kit-capacity");
  const capStale = cap?.payload?.updated
    ? Date.now() - new Date(cap.payload.updated).getTime() > 1500000
    : false;

  return (
    <div data-testid="desk-page" className="w-full font-inter text-os-ink">
      <div
        className={cn(
          desktop
            ? "mx-auto flex max-w-[100rem] flex-col gap-4 px-10 py-6"
            : "flex flex-col gap-3 px-4 pb-8 pt-4",
        )}
      >
        {flash && (
          <p
            data-testid="desk-flash"
            className="text-[0.875rem] font-medium text-os-green"
          >
            {flash}
          </p>
        )}

        {/* 0. Kit capacity — Jeff 10/30: first thing on the page, bar+pct+slots only, 50-slot working total */}
        <KitCapacity compactBar totalOverride={50} />

        {/* 1. Engine Bay (left, larger) | Jeff's task list (right) — Jeff 11/03:
            the composer moved out; the right column is now HIS small one-off
            task list (kind=task rows on the goals board, never executor work) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:items-stretch">
          <div className="lg:col-span-2">
            <EngineBay
              goalCards={blankBay ? [PLACEHOLDER] : goalCards}
              expandedSlug={expandedTileSlug}
              onToggleExpand={(slug) =>
                setExpandedTileSlug((prev) => (prev === slug ? null : slug))
              }
              board={goals}
              projectsBoard={projects}
              onFlash={setFlash}
            />
          </div>
          <div>
            <DeskTasks goalRows={live ? goals.items || [] : []} board={goals} onFlash={setFlash} />
          </div>
        </div>

        {/* 2. Next up — section kept, testing data out until real rows land */}
        <QueueLane
          goalRows={blankBay ? [] : goalRows}
          board={goals}
          projectsBoard={projects}
          onFlash={setFlash}
        />

        {/* 3. Awaiting review — testing data out until real content lands */}
        <ReviewBay blankSlate={blankBay} />

        {/* End of the page per Jeff 10/30 15:xx — everything from City builds down removed */}
        <p data-testid="desk-cap-fresh" className="hidden" />
      </div>

      <p data-testid="desk-capacity-freshness" className="hidden">
        {cap?.payload?.updated && !capStale ? "fresh" : "stale-or-absent"}
      </p>
    </div>
  );
}